// src/hooks/usePinned.ts
//
// Manages /users/{uid}/pinned/{id} — places the user has pinned to
// the Map tab. This is a separate collection from matches because
// pinning expresses a different intent ("show this on my map") from
// matching ("I'm interested in this thing"). Either action can
// happen without the other.
//
// Pinning sources:
//   - Discover detail sheet "Pin" button (any feed item, matched or not)
//   - Matches grid pin icon (already-matched items)
//   - Chat entity detail sheet "Pin" button (chips with location)
//   - Map tab "Unpin" button (removes existing pin)
//
// The doc shape carries enough info to render the pin and bottom
// sheet without a join — title, description, lat/lng, optional
// image_url, optional chat_prompt for "Ask AI" hand-off.

import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';

export interface PinnedItem {
  id: string;
  title: string;
  description?: string;
  image_url?: string | null;
  location: {
    lat: number;
    lng: number;
    address?: string | null;
  };
  place_id?: string;
  chat_prompt?: string;
  /** Where this pin came from. Useful for analytics + future filtering. */
  source: 'discover' | 'match' | 'chat' | 'other';
  pinned_at: Date | null;
}

export interface PinDraft {
  /** Stable id for the pin. Use a deterministic id when possible
   *  (feed item id, match id) so re-pinning same thing dedupes. */
  id: string;
  title: string;
  description?: string;
  image_url?: string | null;
  location: { lat: number; lng: number; address?: string | null };
  place_id?: string;
  chat_prompt?: string;
  source: 'discover' | 'match' | 'chat' | 'other';
}

/** Read all pinned items for the current user. Live snapshot.
 *  Auth-race-resistant: re-attaches the Firestore listener when the
 *  uid changes (e.g. anon → real account upgrade, or cold-start
 *  before auth has resolved). */
export function usePinned() {
  const [pinned, setPinnedState] = useState<PinnedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let unsubSnap: (() => void) | null = null;
    let currentUid: string | null = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid === currentUid) return;
      // Tear down old snapshot before attaching new
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      currentUid = user?.uid || null;
      if (!user) {
        setPinnedState([]);
        setIsLoading(false);
        return;
      }
      const ref = collection(db, 'users', user.uid, 'pinned');
      const q = query(ref, orderBy('pinned_at', 'desc'));
      unsubSnap = onSnapshot(
        q,
        (snap) => {
          const out: PinnedItem[] = [];
          snap.forEach((d) => {
            const data = d.data() as any;
            if (
              !data?.location ||
              typeof data.location.lat !== 'number' ||
              typeof data.location.lng !== 'number'
            ) {
              return;
            }
            out.push({
              id: d.id,
              title: data.title || '',
              description: data.description,
              image_url: data.image_url ?? null,
              location: data.location,
              place_id: data.place_id,
              chat_prompt: data.chat_prompt,
              source: data.source || 'other',
              pinned_at:
                data.pinned_at instanceof Timestamp
                  ? data.pinned_at.toDate()
                  : null,
            });
          });
          setPinnedState(out);
          setIsLoading(false);
        },
        (err) => {
          console.warn('usePinned listener error:', err);
          setIsLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, []);

  return { pinned, isLoading };
}

/** Watch a single pin's existence. Returns true if pinned. */
export function useIsPinned(pinId: string | null | undefined): boolean {
  const [isPinned, setIsPinned] = useState<boolean>(false);
  useEffect(() => {
    let unsubSnap: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      if (!user || !pinId) {
        setIsPinned(false);
        return;
      }
      const ref = doc(db, 'users', user.uid, 'pinned', pinId);
      unsubSnap = onSnapshot(
        ref,
        (snap) => setIsPinned(snap.exists()),
        () => setIsPinned(false)
      );
    });
    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, [pinId]);
  return isPinned;
}

/** Imperative pin/unpin actions. Strips undefined fields before write. */
export function usePinActions() {
  const pin = useCallback(async (draft: PinDraft): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !draft.id) return;
    const docBody: Record<string, any> = {
      title: draft.title,
      description: draft.description,
      image_url: draft.image_url ?? null,
      location: draft.location,
      place_id: draft.place_id,
      chat_prompt: draft.chat_prompt,
      source: draft.source,
      pinned_at: serverTimestamp(),
    };
    // Firestore rejects undefined fields. Strip.
    const clean: Record<string, any> = {};
    for (const [k, v] of Object.entries(docBody)) {
      if (v !== undefined) clean[k] = v;
    }
    try {
      await setDoc(
        doc(db, 'users', user.uid, 'pinned', draft.id),
        clean,
        { merge: true }
      );
    } catch (err) {
      console.warn('usePinActions.pin failed:', err);
    }
  }, []);

  const unpin = useCallback(async (pinId: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !pinId) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'pinned', pinId));
    } catch (err) {
      console.warn('usePinActions.unpin failed:', err);
    }
  }, []);

  return { pin, unpin };
}
